import { put, call, debounce, take, race } from 'redux-saga/effects'

import * as api from 'Service/Api'

import * as types from 'Redux/Types/VendorTypes'
import * as actions from 'Redux/Actions/VendorActions'
import * as glCodeActions from 'Redux/Actions/GLCodeActions'
import * as glCodeTypes from 'Redux/Types/GLCodeTypes'
import * as expenseCategoryActions from 'Redux/Actions/ExpenseCategoryActions'
import * as expenseCategoryTypes from 'Redux/Types/ExpenseCategoryTypes'

const VENDOR_TAG = 'myVendor'
const tagTitleMap = {
  glCode: 'GL code',
  expenseCategory: 'Expense category',
  status: 'Vendor Status',
}

function* addVendorAliasWorker({ payload }) {
  const { ids, filters, ...request } = payload
  try {
    yield call(api.addVendorAlias, { ...request, ids })
    yield put(actions.addVendorAliasSucceeded({ id: ids[0], alias: payload.alias }))
  } catch (error) {
    yield put(actions.addVendorAliasFailed({ id: ids[0], error: new Error(error) }))
  }
}

function* addVendorTagWorker({ payload }) {
  const { alias, id, name, tagName, type, value } = payload
  const tagTitle = tagTitleMap[tagName]

  try {
    if (!alias) {
      if (!name) {
        throw new Error(`Vendor name is required to create ${tagName}`)
      }
      yield put(actions.addVendorAlias({ ids: [id], type, alias: name, [tagName]: value }))
      const [error] = yield race([
        take(types.ADD_VENDOR_ALIAS_FAILED),
        take(types.ADD_VENDOR_ALIAS_SUCCEEDED),
      ])
      if (error) {
        throw error
      }
    }

    const tagParams = { type: { name: tagName }, value }
    yield call(api.createTag, tagParams)

    const mapTwoTagsParams = {
      fromTag: { type: { name: VENDOR_TAG }, value: alias ? alias : name },
      toTag: tagParams,
    }

    yield call(api.mapTwoTags, mapTwoTagsParams)

    yield put(
      actions.addVendorTagSucceeded({
        id,
        alias: alias || name,
        value,
        tagName,
        successMessage: `${tagTitle} added successfully.`,
      }),
    )

    if (tagName === 'glCode') {
      yield put(glCodeActions.getGLCodes())
    }
    if (tagName === 'expenseCategory') {
      yield put(expenseCategoryActions.getExpenseCategories())
    }
  } catch (error) {
    yield put(
      actions.addVendorTagFailed({
        id,
        alias: alias || name,
        tagName,
        error: new Error(`Unable to add ${tagTitle}`),
      }),
    )
  }
}

function* changeVendorAliasWorker({ payload }) {
  const { ids, ...request } = payload
  try {
    yield call(api.changeVendorAlias, { ...request, ids })
    yield put(actions.changeVendorAliasSucceeded({ id: ids[0], alias: payload.alias }))
  } catch (error) {
    yield put(actions.changeVendorAliasFailed({ id: ids[0], error: new Error(error) }))
  }
}

function* changeVendorTagWorker({ payload }) {
  const { id, type, alias, name, value, newValue, tagName } = payload
  const tagTitle = tagTitleMap[tagName]

  try {
    if (!alias) {
      if (!name) {
        throw new Error(`Vendor name is required to create ${tagName}}`)
      }

      yield put(actions.addVendorAlias({ ids: [id], type, alias: name, [tagName]: newValue }))
      const [error] = yield race([
        take(types.ADD_VENDOR_ALIAS_FAILED),
        take(types.ADD_VENDOR_ALIAS_SUCCEEDED),
      ])
      if (error) {
        throw error
      }
    }

    const changeTagMappingsParams = {
      fromTag: { type: { name: VENDOR_TAG }, value: alias ? alias : name },
      toTag: { value, newValue, type: { name: tagName } },
    }

    yield call(api.changeTagMappings, changeTagMappingsParams)

    yield put(
      actions.changeVendorTagSucceeded({
        id,
        alias: alias || name,
        value: newValue,
        tagName,
        successMessage: `${tagTitle} updated successfully.`,
      }),
    )
  } catch (error) {
    yield put(
      actions.changeVendorTagFailed({
        id,
        value,
        tagName,
        alias: alias || name,
        error: new Error(`Unable to udpate ${tagTitle}`),
      }),
    )
  }
}

function* deleteVendorAliasWorker({ payload }) {
  try {
    const { ids, alias, filters } = payload
    const { limit, offset, ...vendorFilters } = filters
    yield call(api.deleteVendorAlias, payload)
    yield put(actions.fetchVendors({ filters: vendorFilters, limit, offset }))
    const [error] = yield race([
      take(types.FETCH_VENDORS_FAILED),
      take(types.FETCH_VENDORS_SUCCEEDED),
    ])
    if (error) {
      throw error
    }
    yield put(actions.deleteVendorAliasSucceeded({ id: ids[0], alias }))
  } catch (error) {
    yield put(actions.deleteVendorAliasFailed(new Error(error)))
  }
}

function* deleteVendorTagWorker({ payload }) {
  const { alias, filters, id, name, tagName, tagObject, type, value } = payload
  const { limit, offset, ...vendorFilters } = filters
  const tagTitle = tagTitleMap[tagName]

  try {
    if (!alias) {
      if (!name) {
        throw new Error(`Vendor name is required to create ${tagName}`)
      }
      yield put(actions.addVendorAlias({ ids: [id], type, alias: name }))
      const [error] = yield race([
        take(types.ADD_VENDOR_ALIAS_FAILED),
        take(types.ADD_VENDOR_ALIAS_SUCCEEDED),
      ])
      if (error) {
        throw error
      }
    }

    const deleteTagMappingsParams = {
      fromTag: { type: { name: VENDOR_TAG }, value: alias ? alias : name },
      toTag: { type: { name: tagName }, value },
    }
    yield call(api.deleteTagMappings, deleteTagMappingsParams)

    if (tagObject && tagName === 'expenseCategory') {
      yield put(expenseCategoryActions.deleteExpenseCategory(tagObject.tagSetId))
      const [error] = yield race([
        take(expenseCategoryTypes.DELETE_EXPENSE_CATEGORY_FAILED),
        take(expenseCategoryTypes.DELETE_EXPENSE_CATEGORY_SUCCEEDED),
      ])
      if (error) {
        throw error
      }
    }
    if (tagObject && tagName === 'glCode') {
      yield put(glCodeActions.deleteGLCode(tagObject.tagSetId))
      const [error] = yield race([
        take(glCodeTypes.DELETE_GL_CODE_FAILED),
        take(glCodeTypes.DELETE_GL_CODE_SUCCEEDED),
      ])
      if (error) {
        throw error
      }
    }
    yield put(actions.fetchVendors({ filters: vendorFilters, limit, offset }))
    const [error] = yield race([
      take(types.FETCH_VENDORS_FAILED),
      take(types.FETCH_VENDORS_SUCCEEDED),
    ])
    if (error) {
      throw error
    }
    yield put(
      actions.deleteVendorTagSucceeded({
        id,
        alias,
        tagName,
        successMessage: `${tagTitle} deleted successfully`,
      }),
    )
  } catch (error) {
    yield put(actions.deleteVendorTagFailed(new Error(`Unable to delete ${tagTitle}`)))
  }
}

function* fetchVendorAliasesWorker() {
  try {
    const response = yield call(api.getTags, VENDOR_TAG)
    yield put(actions.fetchVendorAliasesSucceeded(response))
  } catch (error) {
    yield put(actions.fetchVendorAliasesFailed(new Error(error)))
  }
}

function* fetchVendorsWorker({ payload }) {
  try {
    const response = yield call(api.fetchVendors, payload)
    yield put(actions.fetchVendorsSucceeded(response))
  } catch (errors) {
    yield put(actions.fetchVendorsFailed(errors))
  }
}

export default function* vendorSaga() {
  yield debounce(150, types.ADD_VENDOR_ALIAS, addVendorAliasWorker)
  yield debounce(150, types.ADD_VENDOR_TAG, addVendorTagWorker)
  yield debounce(150, types.CHANGE_VENDOR_ALIAS, changeVendorAliasWorker)
  yield debounce(150, types.CHANGE_VENDOR_TAG, changeVendorTagWorker)
  yield debounce(150, types.DELETE_VENDOR_ALIAS, deleteVendorAliasWorker)
  yield debounce(150, types.DELETE_VENDOR_TAG, deleteVendorTagWorker)
  yield debounce(150, types.FETCH_VENDOR_ALIASES, fetchVendorAliasesWorker)
  yield debounce(150, types.FETCH_VENDORS, fetchVendorsWorker)
}
